// vim: set ts=2 sts=2 sw=2 et:
//
// Normalización al reglamento WRPF Argentina de los strings de división que llegan de afuera
// (bundle de la web, .openlifter de otra tarima, estado persistido en localStorage).
//
// Por qué existe: los bundles generados por la web ANTES del 2026-06-07 traían las bandas Master
// internacionales de 4-5 años (M40-44, M45-49, ...) en vez de las bandas argentinas de 10 años
// (M40-49, ...). Como la pestaña de Resultados agrupa por el string literal de entry.divisions, y
// el handoff preserva el meet local al reabrir el mismo torneo (redux-persist), esos strings viejos
// sobreviven indefinidamente si no se normalizan acá.

import type { Entry } from "../../types/dataTypes";
import type { GlobalState } from "../../types/stateTypes";
import { WRPF_DIVISIONS } from "./wrpfArgentinaRules";

/** Bandas Master legacy (internacional, 4-5 años) -> bandas WRPF Argentina (10 años). */
const LEGACY_TO_ARGENTINA: Record<string, string> = {
  "M40-44": "M40-49",
  "M45-49": "M40-49",
  "M50-54": "M50-59",
  "M55-59": "M50-59",
  "M60-64": "M60-69",
  "M65-69": "M60-69",
  "M70-74": "M70-79",
  "M75-79": "M70-79",
};

export function normalizeDivisionString(division: string): string {
  return LEGACY_TO_ARGENTINA[division.trim()] ?? division;
}

/**
 * Normaliza las divisiones de una entry (con dedup: "M40-44" y "M40-49" colapsan en una).
 * Devuelve la MISMA referencia si no hay cambios, para no generar churn en Redux.
 */
export function normalizeEntryDivisions<T extends { divisions: ReadonlyArray<string> }>(entry: T): T {
  const mapped: string[] = [];
  for (const d of entry.divisions) {
    const n = normalizeDivisionString(d);
    if (!mapped.includes(n)) mapped.push(n);
  }
  const unchanged = mapped.length === entry.divisions.length && mapped.every((d, i) => d === entry.divisions[i]);
  if (unchanged) return entry;
  return { ...entry, divisions: mapped };
}

/**
 * Lista de divisiones del meet para un torneo WRPF Argentina: el reglamento oficial primero, más
 * cualquier división custom no-legacy que ya existiera (no se pierde nada del dropdown).
 */
export function normalizeMeetDivisions(divisions: ReadonlyArray<string>): string[] {
  const out: string[] = [...WRPF_DIVISIONS];
  for (const d of divisions) {
    const n = normalizeDivisionString(d);
    if (!out.includes(n)) out.push(n);
  }
  return out;
}

/**
 * Normaliza divisiones (meet + entries) de un estado completo. Devuelve `null` si no hay nada que
 * cambiar, para que el llamador evite dispatches innecesarios.
 */
export function normalizeGlobalStateDivisions(state: GlobalState): GlobalState | null {
  const meetDivisions = normalizeMeetDivisions(state.meet.divisions);
  const entries = state.registration.entries.map((e) => normalizeEntryDivisions(e as Entry));

  const meetChanged =
    meetDivisions.length !== state.meet.divisions.length ||
    meetDivisions.some((d, i) => d !== state.meet.divisions[i]);
  const entriesChanged = entries.some((e, i) => e !== state.registration.entries[i]);

  if (!meetChanged && !entriesChanged) return null;

  return {
    ...state,
    meet: { ...state.meet, divisions: meetDivisions },
    registration: { ...state.registration, entries },
  };
}
